/**
 * remoteConfig.ts — جلب إعدادات الأدمن (طبقات/ألوان/نماذج) من /api/v1/config
 * وترطيبها قبل تركيب الخريطة. أي فشل ⇒ null ⇒ تعمل الخريطة بالافتراضيات
 * المضمّنة في الكود (الخريطة لا تنكسر أبداً).
 */
import { setColorStops, type ColorStop } from '../services/colorScales';
import type { ForecastGridType } from './weatherLayers';
import { FORECAST_LAYER_IDS } from './weatherLayers';

export interface RemoteLayerConfig {
    layer_key: string;
    enabled: boolean;
    label_ar: string;
    label_en: string | null;
    color: string | null;
    opacity: number | null;
    sort_order: number;
    group_key: string | null;
    icon: string | null;
    color_stops: ColorStop[] | null;
    legend: { title?: string; labels?: string[] } | null;
    /** مفتاح قالب الألوان النشط لهذه الطبقة (مثل "model_2") — عند توفّر عدّة قوالب. */
    active_color_template?: string | null;
}

export interface RemoteModelConfig {
    model_key: string;
    name: string;
    provider: string | null;
    resolution_label: string | null;
    enabled: boolean;
    sort_order: number;
    color: string | null;
}

/** القيم الافتراضية لحركة الجسيمات — قابلة للتحكّم من داشبورد الأدمن (كل الطبقات تشترك بها). */
export interface RemoteParticleConfig {
    density: number;
    speed: number;
    trail: number;
    opacity: number;
    lineWidth: number;
    lineWidthVariation: number;
    lifetime: number;
    color: string;
}

export interface RemoteConfig {
    layers: RemoteLayerConfig[];
    models: RemoteModelConfig[];
    particles: RemoteParticleConfig | null;
    version: number;
}

// حالة الوحدة: تُملأ مرة واحدة قبل تركيب الخريطة.
const layerOverrides = new Map<string, RemoteLayerConfig>();
let remoteModels: RemoteModelConfig[] | null = null;
let remoteParticles: RemoteParticleConfig | null = null;
let hydrated = false;

export async function fetchRemoteConfig(timeoutMs = 3000): Promise<RemoteConfig | null> {
    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        const res = await fetch('/api/v1/config', { signal: controller.signal });
        clearTimeout(timer);
        if (!res.ok) return null;
        const data = (await res.json()) as RemoteConfig;
        if (!Array.isArray(data.layers)) return null;
        return data;
    } catch {
        return null;
    }
}

/** مفتاح النسخة المحفوظة من إعدادات الأدمن في المتصفّح. */
const CACHE_KEY = 'rw_remote_config_v1';

function readCache(): RemoteConfig | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw) as RemoteConfig;
        return Array.isArray(data?.layers) ? data : null;
    } catch {
        return null;
    }
}

function writeCache(config: RemoteConfig): void {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(config));
    } catch { /* حصّة ممتلئة أو تخزين محجوب — الكاش تحسينٌ لا شرط */ }
}

function apply(config: RemoteConfig): void {
    for (const layer of config.layers) {
        layerOverrides.set(layer.layer_key, layer);
        if (
            layer.color_stops
            && layer.color_stops.length > 0
            && (FORECAST_LAYER_IDS as string[]).includes(layer.layer_key)
        ) {
            setColorStops(layer.layer_key as ForecastGridType, layer.color_stops);
        }
    }
    remoteModels = config.models ?? null;
    remoteParticles = config.particles ?? null;
    hydrated = true;
}

/**
 * يطبّق إعدادات الأدمن قبل تركيب الخريطة.
 *
 * ── لماذا كاش محلّي: شاشة الافتتاح كانت تنتظر الشبكة ────────────────────────
 * كانت الدالّة تنتظر `/api/v1/config` **دائماً** (بمهلة تصل إلى ثلاث ثوانٍ) قبل أن
 * ترتفع راية الجهوز — و`App` يعرض شاشة الافتتاح حتى ترتفع. أي أن شعار Sky Radar
 * يبقى على الشاشة طوال رحلة شبكة إلى الأصل، **قبل** أن تبدأ حزمة الخريطة بالتركيب
 * أصلاً. وعلى شبكة بطيئة يعني ذلك ثلاث ثوانٍ من الشعار قبل أن يبدأ أي شيء.
 *
 * والإعدادات لا تتغيّر إلا حين يعدّلها الأدمن — مرّاتٍ في الشهر لا في الدقيقة. فحفظُ
 * نسخة في المتصفّح وتطبيقها فوراً يُلغي الانتظار كلّه لكل زائر عائد، بلا أي فقد:
 * النسخة الطازجة تُجلب في الخلفية وتُحفظ للإقلاع التالي.
 *
 * ── ما ثمن ذلك، ولماذا هو مقبول ─────────────────────────────────────────────
 * تعديلُ الأدمن يظهر عند **الإقلاع التالي** للزائر لا في الحال. البديل — تطبيق
 * الإعدادات الجديدة أثناء الجلسة — يعني تبدّل ألوان الطبقات تحت عين المستخدم بعد
 * أن رآها، وهو أسوأ من تأخّرِ تعديلٍ يقع مرّاتٍ في الشهر. والزيارة الأولى وحدها
 * تنتظر الشبكة كما كانت، فلا يرى أحدٌ ألواناً افتراضية خاطئة.
 */
export async function hydrateRemoteConfig(): Promise<boolean> {
    if (hydrated) return true;

    const cached = readCache();
    if (cached) {
        apply(cached);
        // تحديث صامت للإقلاع القادم — لا ينتظره أحد ولا يغيّر ما يُعرض الآن.
        void fetchRemoteConfig().then((fresh) => { if (fresh) writeCache(fresh); });
        return true;
    }

    const config = await fetchRemoteConfig();
    if (!config) return false;
    writeCache(config);

    for (const layer of config.layers) {
        layerOverrides.set(layer.layer_key, layer);
        if (
            layer.color_stops
            && layer.color_stops.length > 0
            && (FORECAST_LAYER_IDS as string[]).includes(layer.layer_key)
        ) {
            setColorStops(layer.layer_key as ForecastGridType, layer.color_stops);
        }
    }
    remoteModels = config.models ?? null;
    remoteParticles = config.particles ?? null;
    hydrated = true;
    return true;
}

export function isHydrated(): boolean {
    return hydrated;
}

export function getLayerOverride(layerKey: string): RemoteLayerConfig | null {
    return layerOverrides.get(layerKey) ?? null;
}

/** الطبقة مفعّلة من الأدمن؟ (افتراضياً نعم إن لم توجد إعدادات) */
export function isLayerEnabled(layerKey: string): boolean {
    const override = layerOverrides.get(layerKey);
    return override ? override.enabled : true;
}

export function getLayerSortOrder(layerKey: string, fallback = 999): number {
    return layerOverrides.get(layerKey)?.sort_order ?? fallback;
}

/** هل قالب الألوان النشط لهذه الطبقة يطابق المفتاح المُعطى (مثل "model_2")؟ */
export function isLayerColorTemplateActive(layerKey: string, templateKey: string): boolean {
    return layerOverrides.get(layerKey)?.active_color_template === templateKey;
}

export function getRemoteModels(): RemoteModelConfig[] | null {
    return remoteModels;
}

export function getRemoteParticleDefaults(): RemoteParticleConfig | null {
    return remoteParticles;
}
